import { groupBy } from 'lodash';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { MotionModal } from '../../../components/motion-modal/motion-modal';
import { ImageAspectRatioType } from '../../../interfaces';
import { SeriesRecording, SingleRecording } from '../../../queries/ats/recordings/types/recording';
import { ProgramSource } from '../../../queries/ats/titles/types/sources';
import { getPreferredRecording, getSeriesRecordingEpisodes } from '../../../queries/ats/titles/utils/recording-utils';
import { useDetailsImage } from '../use-details-image';
import { FocusedAsset } from '../use-focused-asset';
import { appendSequenceNumbers } from '../utils';
import { ConfirmDeleteEpisodeRecordingScreen } from './delete-episode-recording-screen';
import css from './delete-recording-modal.module.scss';
import { ConfirmDeleteSeasonScreen, DeleteSeasonsRecordingScreen } from './delete-seasons-recording-screen';
import {
  DeleteSeriesRecordingCancelScreen,
  DeleteSeriesRecordingKeepScreen,
  DeleteSeriesRecordingScreen,
} from './delete-series-recording-screen';

interface DeleteRecordingView {
  focusedAsset: FocusedAsset;
  source: ProgramSource;
}

interface DeleteRecordingModalProps extends DeleteRecordingView {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function DeleteRecordingModal({ focusedAsset, source, isOpen, setIsOpen }: DeleteRecordingModalProps) {
  const image = useDetailsImage({
    asset: focusedAsset,
    shape: ImageAspectRatioType.WIDE,
    width: 300,
  });

  const label =
    focusedAsset.type === 'episode'
      ? appendSequenceNumbers(focusedAsset.series.titleName, focusedAsset)
      : focusedAsset.titleName;

  const series = source.recordings.find((rec): rec is SeriesRecording => rec.type === 'series');
  const recording = getPreferredRecording(source, true);

  if (recording === undefined) {
    throw new Error('Recording not found');
  }

  return (
    <MotionModal classNames={{ content: css.modal }} isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
      <MotionModal.CloseButton onClick={() => setIsOpen(false)} />
      {recording.type === 'single' && (
        <DeleteSingleRecordingModalContent
          recording={recording}
          focusedAsset={focusedAsset}
          image={image}
          label={label}
          setIsOpen={setIsOpen}
        />
      )}
      {recording.type === 'linked' && series !== undefined && (
        <DeleteSeriesRecordingModalContent
          recording={{ ...series, episodes: getSeriesRecordingEpisodes(source.recordings, series.seriesId) }}
          focusedAsset={focusedAsset}
          image={image}
          label={label}
          setIsOpen={setIsOpen}
        />
      )}
    </MotionModal>
  );
}

interface DeleteSeriesRecordingModalContentProps extends Omit<DeleteRecordingModalProps, 'source' | 'isOpen'> {
  recording: SeriesRecording;
  image: string;
  label: string;
}

export function DeleteSeriesRecordingModalContent({
  focusedAsset,
  recording,
  image,
  label,
  setIsOpen,
}: DeleteSeriesRecordingModalContentProps) {
  const { t } = useTranslation('recordings');

  const seasons = groupBy(recording.episodes, (rec) => rec.season.sequenceNumber);
  const seasonNumbers = Object.keys(seasons).map((season) => parseInt(season, 10));

  return (
    <Fragment>
      <MotionModal.DefaultView>
        <MotionModal.Header image={image} title={label} />
        <div className={css.container}>
          <Fragment>
            <MotionModal.ViewButton id="episode">{t('delete-episode')}</MotionModal.ViewButton>
            <MotionModal.ViewButton id="seasons">{t('delete-seasons')}</MotionModal.ViewButton>
            <MotionModal.ViewButton id="series">{t('delete-series')}</MotionModal.ViewButton>
          </Fragment>
        </div>
      </MotionModal.DefaultView>

      <MotionModal.View id="episode">
        <MotionModal.Header image={image} title={t('confirm-deletion')} />
        <div className={css.container}>
          <ConfirmDeleteEpisodeRecordingScreen
            focusedAsset={focusedAsset}
            recording={recording.episodes[0]}
            setIsOpen={setIsOpen}
          />
        </div>
      </MotionModal.View>

      <MotionModal.View id="seasons">
        <MotionModal.Header image={image} title={t('delete-seasons')} />
        <div className={css.container}>
          <DeleteSeasonsRecordingScreen focusedAsset={focusedAsset} seasonNumbers={seasonNumbers} />
        </div>
      </MotionModal.View>

      {seasonNumbers.map((season) => (
        <MotionModal.View key={season} id={`season-${season}`}>
          <MotionModal.Header image={image} title={t('confirm-deletion')} />
          <div className={css.container}>
            <ConfirmDeleteSeasonScreen focusedAsset={focusedAsset} recording={recording} season={season} />
          </div>
        </MotionModal.View>
      ))}

      <MotionModal.View id="series">
        <MotionModal.Header image={image} title={t('delete-series')} />
        <div className={css.container}>
          <DeleteSeriesRecordingScreen />
        </div>
      </MotionModal.View>

      <MotionModal.View id="series-keep">
        <MotionModal.Header image={image} title={t('confirm-deletion')} />
        <div className={css.container}>
          <DeleteSeriesRecordingKeepScreen focusedAsset={focusedAsset} recording={recording} />
        </div>
      </MotionModal.View>

      <MotionModal.View id="series-cancel">
        <MotionModal.Header image={image} title={t('confirm-deletion')} />
        <div className={css.container}>
          <DeleteSeriesRecordingCancelScreen focusedAsset={focusedAsset} recording={recording} />
        </div>
      </MotionModal.View>
    </Fragment>
  );
}

interface DeleteSingleRecordingModalContentProps extends Omit<DeleteRecordingModalProps, 'source' | 'isOpen'> {
  recording: SingleRecording;
  image: string;
  label: string;
}

function DeleteSingleRecordingModalContent({
  focusedAsset,
  recording,
  image,
  label,
  setIsOpen,
}: DeleteSingleRecordingModalContentProps) {
  return (
    <Fragment>
      <MotionModal.Header image={image} title={label} />
      <div className={css.container}>
        <ConfirmDeleteEpisodeRecordingScreen focusedAsset={focusedAsset} recording={recording} setIsOpen={setIsOpen} />
      </div>
    </Fragment>
  );
}
